'use client';

import React, { useState } from 'react';

interface Props {
    preview: string;
    full: string;
    faqs: { question: string; answer: string }[];
    categoryName: string;
    children: React.ReactNode; // product grid goes here
}

const CategoryContentExpander = ({ preview, full, faqs, categoryName, children }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const hasMore = full && full !== preview;

    return (
        <>
            {/* ── Preview — above the grid ── */}
            {preview && (
                <div
                    className="mb-8 overflow-hidden"
                    style={{
                        background: 'rgba(8,28,17,0.04)',
                        border: '1px solid rgba(8,28,17,0.08)',
                        clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                    }}
                >
                    <div className="p-5">
                        <p className="text-sm leading-relaxed m-0" style={{ color: 'rgba(8,28,17,0.6)' }}>
                            {preview}
                        </p>
                    </div>
                    {hasMore && (
                        <div className="px-5 pb-4 flex justify-end"
                            style={{ borderTop: '1px solid rgba(8,28,17,0.06)' }}>
                            <button
                                onClick={() => {
                                    if (!expanded) {
                                        setExpanded(true);
                                        setTimeout(() => {
                                            document.getElementById('category-full-description')
                                                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }, 50);
                                    } else {
                                        setExpanded(false);
                                    }
                                }}
                                className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-200"
                                style={{ color: '#081C11', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}
                            >
                                {expanded ? 'Show Less' : 'View More'}
                                <svg
                                    className={`w-3 h-3 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Product grid slot ── */}
            {children}

            {/* ── FAQs — below the grid ── */}
            {faqs?.length > 0 && (
                <div
                    className="mt-10 overflow-hidden"
                    style={{
                        background: '#081C11',
                        clipPath: 'polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px))',
                        border: '1px solid rgba(122,252,32,0.12)',
                    }}
                >
                    <div className="px-8 pt-8 pb-5" style={{ borderBottom: '1px solid rgba(122,252,32,0.08)' }}>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="h-px w-8 bg-gradient-to-r from-[#7AFC20] to-transparent" />
                            <span className="text-[10px] font-black uppercase tracking-[0.3em]"
                                style={{ color: 'rgba(122,252,32,0.5)' }}>
                                Need Help?
                            </span>
                        </div>
                        <h2 className="text-xl md:text-2xl font-black m-0">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7AFC20] to-[#5cd010]">
                                {categoryName}{' '}
                            </span>
                            <span style={{ color: '#F5F5DC' }}>FAQs</span>
                        </h2>
                    </div>

                    <div className="px-6 py-6 space-y-3">
                        {faqs.map((faq, i) => (
                            <details
                                key={`faq-${i}`}
                                className="group overflow-hidden transition-all duration-300"
                                style={{
                                    background: '#0d2416',
                                    border: '1px solid rgba(122,252,32,0.1)',
                                    clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                                }}
                            >
                                <summary className="list-none [&::-webkit-details-marker]:hidden flex justify-between items-center p-5 cursor-pointer select-none gap-4">
                                    <span
                                        className="font-black text-sm leading-snug pr-2 transition-colors duration-200 group-open:text-[#7AFC20]"
                                        style={{ color: '#F5F5DC' }}
                                    >
                                        {faq.question}
                                    </span>
                                    <div
                                        className="flex-shrink-0 w-7 h-7 flex items-center justify-center transition-all duration-300 group-open:rotate-45"
                                        style={{
                                            background: 'rgba(122,252,32,0.1)',
                                            border: '1px solid rgba(122,252,32,0.3)',
                                            color: '#7AFC20',
                                        }}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                                        </svg>
                                    </div>
                                </summary>
                                <div
                                    className="px-5 pb-5 text-sm leading-relaxed"
                                    style={{ color: 'rgba(245,245,220,0.5)', borderTop: '1px solid rgba(122,252,32,0.08)' }}
                                >
                                    <div className="pt-4" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Full description — revealed below FAQs ── */}
            {expanded && full && (
                <div
                    id="category-full-description"
                    className="mt-10 overflow-hidden scroll-mt-6"
                    style={{
                        background: '#081C11',
                        clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))',
                        border: '1px solid rgba(122,252,32,0.12)',
                    }}
                >
                    <div className="px-8 pt-8 pb-5" style={{ borderBottom: '1px solid rgba(122,252,32,0.08)' }}>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="h-px w-8 bg-gradient-to-r from-[#7AFC20] to-transparent" />
                            <span className="text-[10px] font-black uppercase tracking-[0.3em]"
                                style={{ color: 'rgba(122,252,32,0.5)' }}>
                                More Info
                            </span>
                        </div>
                        <h2 className="text-xl font-black m-0">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7AFC20] to-[#5cd010]">
                                About{' '}
                            </span>
                            <span style={{ color: '#F5F5DC' }}>{categoryName}</span>
                        </h2>
                    </div>

                    <div className="p-8 md:p-10">
                        <p className="text-sm leading-relaxed m-0" style={{ color: 'rgba(245,245,220,0.7)' }}>
                            {full}
                        </p>
                    </div>

                    <div className="px-8 pb-6 flex justify-end"
                        style={{ borderTop: '1px solid rgba(122,252,32,0.08)' }}>
                        <button
                            onClick={() => setExpanded(false)}
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-200 hover:text-[#7AFC20]"
                            style={{ color: 'rgba(122,252,32,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}
                        >
                            Show Less
                            <svg className="w-3 h-3 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default CategoryContentExpander;
